import { get, map, reduce } from 'lodash';

import * as constants from '../../constants';
import { getHashingMetadata } from './responseParser';

export default (state = {}, action) => {
  switch (action.type) {
    case constants.FETCH_REQUESTS_SUCCESS:
      return {
        ...state,
        requests: action.payload,
      };
    case constants.FETCH_APP_SECRET_HASHING_METADATA_SUCCESS:
      return {
        ...state,
        appSecretHashingMetadata: getHashingMetadata(get(action, 'payload.Value')),
      };
    case constants.FETCH_DEPLOYMENT_REFRESH_COUNTDOWN_IN_SECONDS_SUCCESS:
      return {
        ...state,
        deploymentRefreshTime: get(action, 'payload.Value'),
      };
    case constants.FETCH_APPLICATION_REQUEST_SETTING_SUCCESS:
      {
        const { requestSettings } = state;
        return {
          ...state,
          requestSettings: {
            ...requestSettings,
            isApplicationRequestWorkflowEnabled: get(action, 'payload'),
          },
        };
      }
    case constants.FETCH_APPLICATION_EDIT_REQUEST_SETTING_SUCCESS:
      {
        const { requestSettings } = state;
        return {
          ...state,
          requestSettings: {
            ...requestSettings,
            isEditApplicationRequestWorkflowEnabled: get(action, 'payload'),
          },
        };
      }
    case constants.FETCH_SETTING_USER_LOCK_CREATION_SUCCESS:
      {
        const { featureFlags } = state;
        return {
          ...state,
          featureFlags: {
            ...featureFlags,
            lockUserCreation: action.payload,
          },
        };
      }
    default:
      return state;
  }
};

export const getConfig = (state) => state.portalConfig && state.portalConfig.config;
export const getUserPrefs = (state) => state.portalConfig && state.portalConfig.userPrefs;
export const getDict = (state) => state.portalConfig && state.portalConfig.dict;
export const getUserContexts = (state) => state.portalConfig && state.portalConfig.userContexts;
export const getUserAccessibleOrgs = (state) =>
  map(
    getUserContexts(state) && getUserContexts(state).userContexts[0].accessibleOrgs,
    (uuid, name) => ({ uuid, name }),
  );
export const getUserActiveOrgUuid = (state) =>
  getUserContexts(state) && getUserContexts(state).userContexts[0].activeOrgUuid;
export const getUserDetails = (state) =>
  getUserContexts(state) && getUserContexts(state).userContexts[0].userDetails;
export const getServices = (state) => state.portalConfig && state.portalConfig.services;
export const getGlobalNavigation = (state) => state.portalConfig &&
  state.portalConfig.globalNavigation;
export const getCQNavigation = (state) => state.portalConfig && state.portalConfig.cqNavigation;
export const getThemes = (state) => state.portalConfig && state.portalConfig.themes;
export const getDisplayCopyright = (state: Object) =>
  get(state, 'portalConfig.themes.display.copyright', true);
export const getPublisherNavigation = (state) => state.portalConfig &&
state.portalConfig.publisherNavigation;
export const getDeveloperNavigations = (state) => state.portalConfig &&
state.portalConfig.developerNavigation;
export const getSettingsNavigation = (state) => state.portalConfig &&
state.portalConfig.settingsNavigation;

export const getFeatureFlags = (state) => state.portalConfig && state.portalConfig.featureFlags;
export const getFeatureFlagApiPlans = (state) => (
  get(getFeatureFlags(state), 'apiPlans.Value') === 'true'
);
export const getUseLegacyApiPages = (state) => (
  get(getFeatureFlags(state), 'useLegacyApiPages.Value') === 'true'
);
export const getUseLegacyApplicationPages = (state) => (
  get(getFeatureFlags(state), 'useLegacyApplicationPages.Value') === 'true'
);
export const getNewLandingPage = (state) => (
  get(getFeatureFlags(state), 'newLandingPage.Value') === 'true'
);
export const getUseMultiApiKeys = (state) => (
  get(getFeatureFlags(state), 'useMultiApiKeys.Value') === 'true'
);
export const getLockUserCreation = (state) => (
  get(getFeatureFlags(state), 'lockUserCreation.Value') === 'true'
);

export const getRequestSettings = (state) => (
  get(state, 'portalConfig.requestSettings')
);
export const getIsApplicationRequestWorkflowEnabled = (state) => (
  get(getRequestSettings(state), 'isApplicationRequestWorkflowEnabled.Value') === 'ENABLED'
);
export const getIsEditApplicationRequestWorkflowEnabled = (state) => (
  get(getRequestSettings(state), 'isEditApplicationRequestWorkflowEnabled.Value') === 'ENABLED'
);

export const getRequests = (state) => state.portalConfig && state.portalConfig.requests;
export const getAppSecretHashingMetadata = (state) => get(state, 'portalConfig.appSecretHashingMetadata');
export const getDeploymentRefreshTime = (state) => get(state, 'portalConfig.deploymentRefreshTime');

const reducePortalAdminAuthorities = (acc: boolean, value: Object) => {
  const adminAuthorities = ['portaladministrators', 'apiowners'];
  return (acc || adminAuthorities.includes(value.authority));
};

export const getIsPortalAdmin = (state: Object) =>
  getUserDetails(state) &&
  getUserDetails(state).authorities &&
  reduce(getUserDetails(state).authorities, reducePortalAdminAuthorities, false);

const reduceOnlyPortalAdminAuthorities = (acc: boolean, value: Object) => {
  const adminAuthorities = ['portaladministrators'];
  return (acc || adminAuthorities.includes(value.authority));
};

export const getIsOnlyPortalAdmin = (state: Object) =>
  getUserDetails(state) &&
  getUserDetails(state).authorities &&
  reduce(getUserDetails(state).authorities, reduceOnlyPortalAdminAuthorities, false);

const reduceApiOwnerAuthorities = (acc: boolean, value: Object) => {
  const adminAuthorities = ['apiowners'];
  return (acc || adminAuthorities.includes(value.authority));
};

export const getIsApiOwner = (state: Object) =>
  getUserDetails(state) &&
  getUserDetails(state).authorities &&
  reduce(getUserDetails(state).authorities, reduceApiOwnerAuthorities, false);

const reduceOrgPublisherAuthorities = (acc: boolean, value: Object) => {
  const adminAuthorities = ['orgpublishers'];
  return (acc || adminAuthorities.includes(value.authority));
};

export const getIsOrgPublisher = (state: Object) =>
  getUserDetails(state) &&
  getUserDetails(state).authorities &&
  reduce(getUserDetails(state).authorities, reduceOrgPublisherAuthorities, false);

export const getIsAnonymousRole = (state: Object) =>
  getUserDetails(state) &&
  getUserDetails(state).authorities &&
  (getUserDetails(state).authorities.length === 0);

export const getCookieConsent = (state) =>
  get(state, 'portalConfig.themes.display.cookieConsent');
export const getThemeBrandedLabels = (state) =>
  get(state, 'portalConfig.themes.label');



// WEBPACK FOOTER //
// ./src/reducers/portalConfig/index.js